import { EditableCallbackParams } from 'ag-grid-community'
import {
  SettlementVenue,
  CollateralBasketType,
  Market,
  AssetType,
  RepoType,
  CallableType,
  RepoClassification,
  NegotiationType,
  TradingType,
  NestedLegItem,
  PlatformFeature,
  RfqType,
  NegotiationStatus,
  NegotiationFieldMeta,
  StockBorrowingLendingType
} from 'api'
import { AuditDetail, NegotiationDetail, NegotiationPackageDetail, SummaryRowDirection } from 'api/local'
import { isAonOrOcoCollection, getNegotiatedValue } from 'model'
import { TypedEditableCallbackParams } from 'view/components/datagrid'
import {
  canUpdateAttributes,
  isClearedNegotiation,
  isNotHeaderRow,
  isAuditDetail,
  isSummaryNegotiationType,
  canUpdatePreAssignmentAttributes,
  isEnhancedSummaryRowNegotiation
} from 'view/routes/requests/shared/utils'
import { negotiationRowDataGetter } from '../groupRowFormatters'
import { makeCanEditPreTradeAssignmentDetailField } from './common'

export const canEditNegotiationDetailRowSettlementType = (params: TypedEditableCallbackParams<NegotiationDetail>) => {
  const negotiationDetail = negotiationRowDataGetter(params)
  const collectionType = negotiationDetail?.collection?.type
  const notTripartySettlement = negotiationDetail?.negotiation.settlementVenueNegotiable.original !== SettlementVenue.Triparty

  if (isAonOrOcoCollection(collectionType)) {
    return notTripartySettlement && canEditNegotiationDetailSummary(params) && canEditNegotiationDetailSettlementType(negotiationDetail)
  } else {
    return notTripartySettlement && canEditNegotiationDetailGeneral(params) && canEditNegotiationDetailSettlementType(negotiationDetail)
  }
}

export function canEditNegotiationDetailSettlementType(n: NegotiationDetail | null) {
  return (
    n?.usSponsoredRelationship === true &&
    n?.productSector?.basketType !== CollateralBasketType.BONY &&
    n?.productSector?.basketType !== CollateralBasketType.GC_PLUS &&
    ((n?.negotiation.market === Market.US && n.collateral?.assetType !== AssetType.CORP) ||
      n?.productSector?.basketType === CollateralBasketType.GC_BESPOKE) &&
    n.negotiation.market !== Market.EUROPE &&
    n.negotiation.repoType === RepoType.CLASSIC &&
    n.negotiation.callableType === CallableType.NONE &&
    n.negotiation.repoClassification === RepoClassification.FIXED
  )
}

function canEditNegotiationDetailSummary(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return !!params.node.group && !!rowData && canUpdateAttributes(rowData)
}

export function canEditNegotiationDetailGeneral(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return !params.node.group && !!rowData && canUpdateAttributes(rowData)
}

export function canEditNegotiationsDetailBookStrategy(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)

  return canEditNegotiationDetailGeneral(params) && !hasNegotiationNestedLegs(rowData)
}

export function getNegotiationDetailHaircutEditable(negotiationDetail: NegotiationDetail) {
  return (
    (negotiationDetail?.usSponsoredRelationship === true &&
      negotiationDetail?.negotiation.market === Market.US &&
      negotiationDetail.productSector?.basketType !== CollateralBasketType.GC_BESPOKE) ||
    negotiationDetailIsNonClearedNonGC(negotiationDetail)
  )
}

export function isNegotiationDetailAuditDetailNonClearedNonGC(negotiationDetail: NegotiationDetail | AuditDetail) {
  return !isClearedNegotiation(negotiationDetail) && negotiationDetail.productSector?.basketType !== CollateralBasketType.GC_BESPOKE
}

export function negotiationDetailIsNonClearedNonGC(negotiationDetail: NegotiationDetail) {
  return (
    isNegotiationDetailAuditDetailNonClearedNonGC(negotiationDetail) &&
    getNegotiatedValue(negotiationDetail.negotiation.settlementVenueNegotiable) !== SettlementVenue.Sponsored &&
    getNegotiatedValue(negotiationDetail.negotiation.settlementVenueNegotiable) !== SettlementVenue.Direct
  )
}

export const isMultiDirectionSummary = (negotiationDetail: NegotiationDetail) => {
  if (
    isSummaryNegotiationType(negotiationDetail.negotiation.negotiationType) &&
    isEnhancedSummaryRowNegotiation(negotiationDetail.negotiation) &&
    negotiationDetail.negotiation.summaryDirection === SummaryRowDirection.MULTIPLE
  ) {
    return true
  }
  return false
}

export function canEditNegotiationDetailHaircut(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  const isGroupRow = params.node.group

  if (!rowData) {
    return false
  }

  if (isMultiDirectionSummary(rowData)) {
    return false
  }

  const isAonSummary = rowData.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY
  const isSmartGroupingSummary = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY

  return (
    canUpdateAttributes(rowData) && (!isGroupRow || isAonSummary || isSmartGroupingSummary) && getNegotiationDetailHaircutEditable(rowData)
  )
}

export function canEditNegotiationDetailHaircutType(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  const isGroupRow = params.node.group

  if (!rowData) {
    return false
  }

  const isSmartGroupingSummary = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY

  return canUpdateAttributes(rowData) && (!isGroupRow || isSmartGroupingSummary) && getNegotiationDetailHaircutEditable(rowData)
}

export function canEditNegotiationDetailRate(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  const isGroupRow = params.node.group

  if (!rowData) {
    return false
  }

  const isAonSummary = rowData.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY
  const isSmartGroupingSummary = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY
  const isSblTrade = rowData.negotiation.repoType === RepoType.STOCK_BORROWING_LENDING

  return canUpdateAttributes(rowData) && (!isGroupRow || isAonSummary || isSmartGroupingSummary) && !isSblTrade
}

export function canEditNegotiationDetailFee(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)

  if (!rowData) {
    return false
  }

  return (
    canUpdateAttributes(rowData) &&
    (!params.node.group || rowData.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY) &&
    rowData.negotiation.repoType === RepoType.STOCK_BORROWING_LENDING
  )
}

export function canEditNegotiationDetailSecondaryCollateral(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  if (!rowData) {
    return false
  }

  const isGcBespokeSbl =
    rowData.productSector?.basketType === CollateralBasketType.GC_BESPOKE &&
    rowData.negotiation.repoType === RepoType.STOCK_BORROWING_LENDING

  const isSingleSbl =
    rowData.negotiation.repoType === RepoType.STOCK_BORROWING_LENDING &&
    getNegotiatedValue(rowData.negotiation.stockBorrowingLendingType) === StockBorrowingLendingType.SINGLE

  return canUpdateAttributes(rowData) && isNotHeaderRow(params) && (isGcBespokeSbl || isSingleSbl)
}

export function getNegotiationDetailBondPriceEditable(negotiationDetail: NegotiationDetail | AuditDetail) {
  return negotiationDetail.negotiation.basketAllocation === null
}

export function canEditNegotiationDetailBondPrice(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return !!rowData && canUpdateAttributes(rowData) && getNegotiationDetailBondPriceEditable(rowData) && !params.node.group
}

export function getNegotiationDetailNotionalEditable(negotiationDetail: NegotiationDetail | AuditDetail) {
  // TODO remove first condition when we enforce DBV to have trading type notional
  return (
    !isAuditDetail(negotiationDetail) &&
    (negotiationDetail.productSector?.basketType === CollateralBasketType.DBV ||
      negotiationDetail.negotiation.tradingType !== TradingType.CASH)
  )
}

export function hasNegotiationNestedLegs(negotiationDetail: NegotiationDetail | null | undefined) {
  return !!(negotiationDetail && negotiationDetail?.negotiation?.nestedLegs?.legs?.length)
}

export function canEditNegotiationDetailNotional(params: TypedEditableCallbackParams<NegotiationDetail>) {
  if (params.node.group) {
    return false
  }
  const rowData = negotiationRowDataGetter(params)
  return !!rowData && canUpdateAttributes(rowData) && getNegotiationDetailNotionalEditable(rowData) && !hasNegotiationNestedLegs(rowData)
}

export function canEditNegotiationPreAssignmentNotional(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.NOTIONAL)(params)) {
    return false
  }

  const negotiationDetail = getNegotiationDetailFromAgGridContext(params)

  return canUpdatePreAssignmentAttributes(negotiationDetail) && getNegotiationDetailNotionalEditable(negotiationDetail)
}

export function getNegotiationDetailStartConsiderationEditable(negotiationDetail: NegotiationDetail | AuditDetail) {
  // TODO remove last condition when we enforce DBV to have trading type notional
  return (
    !isAuditDetail(negotiationDetail) &&
    negotiationDetail.negotiation.tradingType !== TradingType.NOTIONAL &&
    negotiationDetail.productSector?.basketType !== CollateralBasketType.DBV
  )
}

export function canEditNegotiationDetailStartConsideration(params: TypedEditableCallbackParams<NegotiationDetail>) {
  if (params.node.group) {
    return false
  }
  const rowData = negotiationRowDataGetter(params)
  return (
    !!rowData &&
    canUpdateAttributes(rowData) &&
    getNegotiationDetailStartConsiderationEditable(rowData) &&
    !hasNegotiationNestedLegs(rowData)
  )
}

export function canEditNegotiationPreAssignmentStartConsideration(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.START_CONSIDERATION)(params)) {
    return false
  }

  const negotiationDetail = getNegotiationDetailFromAgGridContext(params)

  return canUpdatePreAssignmentAttributes(negotiationDetail) && getNegotiationDetailStartConsiderationEditable(negotiationDetail)
}

export function getInitiatedNegotiationDetailStartConsiderationOverrideEditable(negotiationDetail: NegotiationDetail | AuditDetail) {
  return (
    !isAuditDetail(negotiationDetail) &&
    negotiationDetail.productSector?.startConsiderationOverrideThreshold !== 0 &&
    !!negotiationDetail.counterpartyFirm?.enabledFeatures.includes(PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE)
  )
}

export function canEditInitiatedNegotiationDetailStartConsiderationOverride(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return (
    !!rowData &&
    canUpdateAttributes(rowData) &&
    getInitiatedNegotiationDetailStartConsiderationOverrideEditable(rowData) &&
    canEditNegotiationDetailStartConsideration(params)
  )
}

export function canEditInitiatedNegotiationPreAssignmentStartConsiderationOverride(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE)(params)) {
    return false
  }

  const negotiationDetail = getNegotiationDetailFromAgGridContext(params)

  return (
    canUpdatePreAssignmentAttributes(negotiationDetail) &&
    getInitiatedNegotiationDetailStartConsiderationOverrideEditable(negotiationDetail) &&
    getNegotiationDetailStartConsiderationEditable(negotiationDetail)
  )
}

export function canEditNegotiationPreAssignmentInitiatorBook(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.INITIATOR_BOOK)(params)) {
    return false
  }

  const negotiationDetail = getNegotiationDetailFromAgGridContext(params)

  return canUpdatePreAssignmentAttributes(negotiationDetail)
}

export function canEditNegotiationPreAssignmentInitiatorStrategy(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.INITIATOR_STRATEGY)(params)) {
    return false
  }

  const negotiationDetail = getNegotiationDetailFromAgGridContext(params)

  return canUpdatePreAssignmentAttributes(negotiationDetail)
}

export function getIncomingNegotiationDetailStartConsiderationOverrideEditable(negotiationDetail: NegotiationDetail | AuditDetail) {
  return (
    !isAuditDetail(negotiationDetail) &&
    negotiationDetail.productSector?.startConsiderationOverrideThreshold !== 0 &&
    !!negotiationDetail.orderFundFirm?.enabledFeatures.includes(PlatformFeature.ENABLE_START_CONSIDERATION_OVERRIDE)
  )
}

export function canEditIncomingNegotiationDetailStartConsiderationOverride(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return (
    !!rowData &&
    canUpdateAttributes(rowData) &&
    getIncomingNegotiationDetailStartConsiderationOverrideEditable(rowData) &&
    canEditNegotiationDetailStartConsideration(params)
  )
}

export function canEditIncomingNegotiationPreAssignmentStartConsiderationOverride(params: EditableCallbackParams<NestedLegItem>) {
  if (!makeCanEditPreTradeAssignmentDetailField(NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE)(params)) {
    return false
  }

  const negotiationDetail = getNegotiationDetailFromAgGridContext(params)

  return (
    canUpdatePreAssignmentAttributes(negotiationDetail) &&
    getIncomingNegotiationDetailStartConsiderationOverrideEditable(negotiationDetail) &&
    getNegotiationDetailStartConsiderationEditable(negotiationDetail)
  )
}

export function getNegotiationDetailOtwEditable(negotiationDetail: NegotiationDetail) {
  return ![NegotiationType.CONTINGENT_ITEM, NegotiationType.ONE_CANCELS_OTHERS_SUMMARY].includes(
    negotiationDetail.negotiation.negotiationType
  )
}

export function canEditNegotiationDetailOtw(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return !!rowData && canUpdateAttributes(rowData) && getNegotiationDetailOtwEditable(rowData)
}

export function isFixedRepo(negotiationDetail: NegotiationDetail) {
  return negotiationDetail.negotiation.repoClassification === RepoClassification.FIXED
}

export function canEditIncomingNegotiationDetailEndDate(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return !!rowData && canEditNegotiationDetailGeneral(params) && isFixedRepo(rowData)
}

export function canEditNegotiationDetailEndDate(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  const isGroupRow = params.node.group

  if (!rowData) {
    return false
  }

  const isSmartGroupingSummary = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY
  const isSmartGroupingItem = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_ITEM
  const isFixedRepo = rowData.negotiation.repoClassification === RepoClassification.FIXED

  return canUpdateAttributes(rowData) && (!isGroupRow || isSmartGroupingSummary) && isFixedRepo && !isSmartGroupingItem
}

export function canEditNegotiationDetailRFQType(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return (
    !!rowData &&
    canUpdateAttributes(rowData) &&
    ![NegotiationType.CONTINGENT_ITEM, NegotiationType.ONE_CANCELS_OTHERS_SUMMARY].includes(rowData.negotiation.negotiationType) &&
    rowData.negotiation.rfqType.negotiated === RfqType.INDICATIVE
  )
}

export function canEditNegotiationDetailCounterpartyId(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return (
    !!rowData &&
    canUpdateAttributes(rowData) &&
    [
      NegotiationType.CONTINGENT_SUMMARY,
      NegotiationType.ONE_CANCELS_OTHERS_SUMMARY,
      NegotiationType.INDIVIDUAL,
      NegotiationType.SMART_GROUPING_ITEM
    ].includes(rowData.negotiation.negotiationType)
  )
}

export function canEditNegotiationDetailCounterpartySponsor(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return (
    !!rowData &&
    canEditNegotiationDetailGeneral(params) &&
    rowData.negotiation.clearingHouseId !== null &&
    rowData.negotiation.market !== Market.US
  )
}

export function canEditNegotiationPreAgreed(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  return !!rowData && canUpdateAttributes(rowData) && rowData.negotiation.negotiationType === NegotiationType.INDIVIDUAL
}

export function canEditNegotiationDetailIdmEdm(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const canEditGeneral = canEditNegotiationDetailGeneral(params)
  const negotiation = negotiationRowDataGetter(params)?.negotiation
  const negotiationStatus = [NegotiationStatus.REQUESTED_WITHOUT_PRICE, NegotiationStatus.REQUESTED_WITH_PRICE]
  const isGroupRow = params.node.group

  if (isGroupRow) {
    return false
  }

  return (canEditGeneral && !!negotiation) || (negotiation?.status ? negotiationStatus.includes(negotiation.status) : false)
}

export function canEditNegotiationStartDate(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  const isGroupRow = params.node.group

  if (!rowData) {
    return false
  }

  const isSmartGroupingSummary = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY
  const isSmartGroupingItem = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_ITEM

  return canUpdateAttributes(rowData) && (!isGroupRow || isSmartGroupingSummary) && !isSmartGroupingItem
}

export function canEditNegotiationTerm(params: TypedEditableCallbackParams<NegotiationDetail>) {
  const rowData = negotiationRowDataGetter(params)
  const isGroupRow = params.node.group

  if (!rowData) {
    return false
  }

  const isSmartGroupingSummary = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY
  const isSmartGroupingItem = rowData.negotiation.negotiationType === NegotiationType.SMART_GROUPING_ITEM

  return canUpdateAttributes(rowData) && (!isGroupRow || isSmartGroupingSummary) && !isSmartGroupingItem
}

/* Negotiation Packages */

export const getNegotiationPackageNotionals = ({ negotiations }: NegotiationPackageDetail) =>
  Object.values(negotiations).map(n => getNegotiatedValue(n.negotiation.notional))

export const negotiationPackageHasNotionalEquality = (negotiationPackage: NegotiationPackageDetail) =>
  new Set(getNegotiationPackageNotionals(negotiationPackage)).size === 1

/* Misc */

export function getTargetValueIfNotSummary(rowData: NegotiationDetail | null, value?: Date | number | string | boolean | null) {
  return rowData && !isSummaryNegotiationType(rowData.negotiation.negotiationType) ? value : null
}

export function getTargetValueIfNotOcoSummary(rowData: NegotiationDetail | null, value?: Date | number | string | boolean | null) {
  return rowData && rowData.negotiation.negotiationType !== NegotiationType.ONE_CANCELS_OTHERS_SUMMARY ? value : null
}

export function getNegotiationDetailFromAgGridContext<T extends { context: any }>(params: T): NegotiationDetail {
  if (!params.context.negotiationDetail) {
    throw new Error('negotiationDetail is missing on detail grid context')
  } else {
    const negotiationDetail = params.context.negotiationDetail as NegotiationDetail

    return negotiationDetail
  }
}
